
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiUrls } from '../../utils/apiUrls';
import { API, callAPI } from '../../utils/apiUtils';
import { ErrorMessage } from '../../components/common/Helper';

const removeCustomizationFromLocalStorage = (productId) => {
    if (productId) {
        localStorage.removeItem(`product_customization_${productId}`);
    }
};

const transformCartItems = (data) => {
    if (!data) return [];

    const items = data.data || data;

    if (!Array.isArray(items)) return [];
    return items.map(item => ({
        id: item.id,
        cart_id: item.id,
        productId: item.product_id,
        name: item.product?.name || 'Unnamed Product',
        price: parseFloat(item.product?.price || 0),
        originalPrice: parseFloat(item.product?.price || 0),
        printingPrice: parseFloat(item.printing_price || 0),
        quantity: parseInt(item.quantity) || 1,
        size: item.size?.name || item.size_id || null,
        sizeId: item.size_id,
        color: item.color?.name || item.color_id || null,
        colorId: item.color_id,
        colorCode: item.color?.code || null,
        image: item.product?.product_images?.[0]?.image_url ||
            item.product?.image ||
            '/image/placeholder.jpg',
        category: item.product?.category?.title || 'Uncategorized',
        gender: item.gender,
        product: item.product,
        maxQuantity: parseInt(item.product?.stock_quantity) || 10,
        isSaved: item.is_saved || false,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        logos: item.logos && Array.isArray(item.logos) ? item.logos.map(logo => ({
            id: logo.id,
            file: logo.logo,
            fileUrl: logo.logo_url,
            name_file: logo.name_file,
            number_file: logo.number_file,
            nameFileUrl: logo.name_file_url,
            numberFileUrl: logo.number_file_url,
            logo_vector: logo.logo_vector,
            logo_vector_url: logo.logo_vector_url,
            name_vector: logo.name_vector,
            name_vector_url: logo.name_vector_url,
            number_vector: logo.number_vector,
            number_vector_url: logo.number_vector_url,
            previewUrl: logo.logo_preview_url,
            name: logo.logo_name,
            number: logo.logo_number,
            position: logo.position || '',
            preview: logo.preview || null,
        })) : []
    }));
};

export const reorderToCart = createAsyncThunk(
    'cart/reorderToCart',
    async (orderData, { rejectWithValue, dispatch }) => {
        try {

            await dispatch(clearCart());

            const promises = orderData.items.map(async (item) => {
                const formData = new FormData();

                formData.append('product_id', item.product_id);
                formData.append('quantity', item.quantity);
                formData.append('size_id', item.size_id);
                formData.append('color_id', item.color_id);

                if (item.gender) {
                    formData.append('gender', item.gender);
                }

                if (item.logos && Array.isArray(item.logos)) {
                    item.logos.forEach((logo, index) => {
                        formData.append(`logos[${index}][name]`, logo.name || '');
                        formData.append(`logos[${index}][number]`, logo.number || '');
                        formData.append(`logos[${index}][position]`, logo.position || 'front');
                        formData.append(`logos[${index}][logo_id]`, logo.id || '');
                    });
                }

                const response = await API(
                    apiUrls.cartAdd,
                    {},
                    "POST",
                    formData,
                    true
                );

                return response.data;
            });

            const results = await Promise.all(promises);

            return {
                success: true,
                message: 'Items added to cart successfully',
                items: results
            };

        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to reorder items');
        }
    }
);

export const fetchCart = createAsyncThunk(
    'cart/fetchCart',
    async (_, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                apiUrls.cartList,
                {},
                "GET",
                {}
            );

            if (response?.data?.success) {
                return transformCartItems(response.data.data);
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch cart');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const addToCart = createAsyncThunk(
    'cart/addToCart',
    async (itemData, { rejectWithValue, dispatch }) => {
        try {

            const formData = new FormData();

            formData.append('product_id', String(itemData.product_id));
            formData.append('quantity', String(itemData.quantity));
            formData.append('size_id', String(itemData.size_id));
            formData.append('color_id', String(itemData.color_id));
            formData.append('printing_method_id', String(itemData.printing_method_id));

            if (itemData.gender) {
                formData.append('gender', itemData.gender);
            }
             if (!itemData.logos || itemData.logos.length < 1) {
                dispatch(setCartLoading(false));
                return rejectWithValue("Please customize your product before adding to cart");
            }

            if (itemData.logos && Array.isArray(itemData.logos)) {
                for (let index = 0; index < itemData.logos.length; index++) {
                    const logo = itemData.logos[index];

                    const trimmedName = logo.name ? logo.name.toString().trim() : '';
                    const trimmedNumber = logo.number ? logo.number.toString().trim() : '';

                    const hasValidName = trimmedName !== '' && trimmedName != null;
                    const hasValidNumber = trimmedNumber !== '' && trimmedName != null;

                    console.log("hasValidName ", hasValidName, "index ", index)

                    const hasFiles = (logo.file && logo.file instanceof File) ||
                        (logo.preview && logo.preview instanceof File) ||
                        (logo.name_file && logo.name_file instanceof File) ||
                        (logo.number_file && logo.number_file instanceof File) ||
                        (logo.logo_vector && logo.logo_vector instanceof File) ||
                        (logo.name_vector && logo.name_vector instanceof File) ||
                        (logo.number_vector && logo.number_vector instanceof File);

                    if (!hasValidName && !hasValidNumber && !hasFiles) {
                        continue;
                    }

                    formData.append(`logos[${index}][name]`, trimmedName);
                    formData.append(`logos[${index}][number]`, trimmedNumber);
                    formData.append(`logos[${index}][position]`, logo.position || 'front');

                    if (logo.file && logo.file instanceof File) {
                        formData.append(`logos[${index}][file]`, logo.file);
                    }

                    if (logo.preview && logo.preview instanceof File) {
                        formData.append(`logos[${index}][preview]`, logo.preview);
                    }

                    if (hasValidName && logo.name_file && logo.name_file instanceof File) {
                        formData.append(`logos[${index}][name_file]`, logo.name_file);
                    }

                    if (hasValidNumber && logo.number_file && logo.number_file instanceof File) {
                        formData.append(`logos[${index}][number_file]`, logo.number_file);
                    }

                    if (logo.logo_vector && logo.logo_vector instanceof File) {
                        formData.append(`logos[${index}][logo_vector]`, logo.logo_vector);
                    }

                    if (hasValidName && logo.name_vector && logo.name_vector instanceof File) {
                        formData.append(`logos[${index}][name_vector]`, logo.name_vector);
                    }

                    if (hasValidNumber && logo.number_vector && logo.number_vector instanceof File) {
                        formData.append(`logos[${index}][number_vector]`, logo.number_vector);
                    }
                }
            }

            for (let pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(`  ${pair[0]} = [File: ${pair[1].name}, size: ${pair[1].size}]`);
                } else {
                    console.log(`  ${pair[0]} = "${pair[1]}"`);
                }
            }

            const response = await API(
                apiUrls.cartAdd,
                {},
                "POST",
                formData,
                true
            );


            if (response?.data?.success) {
                const transformedItem = transformCartItems([response.data.data])[0];
                return {
                    item: transformedItem,
                    message: response.data.message || 'Item added to cart'
                };
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to add item to cart');
            }
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                'Network error occurred'
            );
        }
    }
);

export const updateCartItem = createAsyncThunk(
    'cart/updateCartItem',
    async (updateData, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            formData.append('cart_id', updateData.cartId);
            formData.append('quantity', updateData.quantity);

            if (updateData.sizeId !== null && updateData.sizeId !== undefined) {
                formData.append('size_id', updateData.sizeId);
            }
            if (updateData.colorId !== null && updateData.colorId !== undefined) {
                formData.append('color_id', updateData.colorId);
            }
            if (updateData.gender !== null && updateData.gender !== undefined) {
                formData.append('gender', updateData.gender);
            }

            if (updateData.logos && Array.isArray(updateData.logos)) {
                updateData.logos.forEach((logo, index) => {
                    if (logo.file) {
                        formData.append(`logos[${index}][file]`, logo.file);
                    }
                    if (logo.name) {
                        formData.append(`logos[${index}][name]`, logo.name);
                    }
                    if (logo.number) {
                        formData.append(`logos[${index}][number]`, logo.number);
                    }
                    formData.append(`logos[${index}][position]`, logo.position || 'front');
                    if (logo.name_file && logo.name_file instanceof File) {
                        formData.append(`logos[${index}][name_file]`, logo.name_file);
                    }

                    if (logo.number_file && logo.number_file instanceof File) {
                        formData.append(`logos[${index}][number_file]`, logo.number_file);
                    }
                    if (logo.logo_vector && logo.logo_vector instanceof File) {
                        formData.append(`logos[${index}][logo_vector]`, logo.logo_vector);
                    }
                    if (logo.name_vector && logo.name_vector instanceof File) {
                        formData.append(`logos[${index}][name_vector]`, logo.name_vector);
                    }
                    if (logo.number_vector && logo.number_vector instanceof File) {
                        formData.append(`logos[${index}][number_vector]`, logo.number_vector);
                    }

                });
            }

            if (updateData.removeLogoIds && Array.isArray(updateData.removeLogoIds)) {
                updateData.removeLogoIds.forEach((logoId, index) => {
                    formData.append(`remove_logo_ids[${index}]`, logoId);
                });
            }

            const response = await callAPI(
                apiUrls.cartUpdate,
                {},
                "POST",
                formData,
                true
            );

            if (response?.data?.success) {
                const transformedItem = transformCartItems([response.data.data])[0];
                return {
                    cartId: updateData.cartId,
                    quantity: updateData.quantity,
                    item: transformedItem,
                    message: response.data.message || 'Cart updated successfully'
                };
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to update cart');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const updateCartLogos = createAsyncThunk(
    'cart/updateCartLogos',
    async (updateData, { rejectWithValue }) => {
        try {
            const formData = new FormData();

            // Only difference from addToCart: add cart_id
            formData.append('cart_id', String(updateData.cartId));
            formData.append('product_id', String(updateData.product_id));
            formData.append('quantity', String(updateData.quantity));
            formData.append('size_id', String(updateData.size_id));
            formData.append('color_id', String(updateData.color_id));

            if (updateData.gender) {
                formData.append('gender', updateData.gender);
            }

            if (updateData.logos && Array.isArray(updateData.logos)) {
                for (let index = 0; index < updateData.logos.length; index++) {
                    const logo = updateData.logos[index];

                    const trimmedName = logo.name ? logo.name.toString().trim() : '';
                    const trimmedNumber = logo.number ? logo.number.toString().trim() : '';

                    const hasValidName = trimmedName !== '' && trimmedName != null;
                    const hasValidNumber = trimmedNumber !== '' && trimmedNumber != null;

                    const hasFiles = (logo.file && logo.file instanceof File) ||
                        (logo.preview && logo.preview instanceof File) ||
                        (logo.name_file && logo.name_file instanceof File) ||
                        (logo.number_file && logo.number_file instanceof File) ||
                        (logo.logo_vector && logo.logo_vector instanceof File) ||
                        (logo.name_vector && logo.name_vector instanceof File) ||
                        (logo.number_vector && logo.number_vector instanceof File);

                    if (!hasValidName && !hasValidNumber && !hasFiles) {
                        continue;
                    }

                    formData.append(`logos[${index}][name]`, trimmedName);
                    formData.append(`logos[${index}][number]`, trimmedNumber);
                    formData.append(`logos[${index}][position]`, logo.position || 'front');

                    if (logo.file && logo.file instanceof File) {
                        formData.append(`logos[${index}][file]`, logo.file);
                    }

                    if (logo.preview && logo.preview instanceof File) {
                        formData.append(`logos[${index}][preview]`, logo.preview);
                    }

                    if (hasValidName && logo.name_file && logo.name_file instanceof File) {
                        formData.append(`logos[${index}][name_file]`, logo.name_file);
                    }

                    if (hasValidNumber && logo.number_file && logo.number_file instanceof File) {
                        formData.append(`logos[${index}][number_file]`, logo.number_file);
                    }

                    if (logo.logo_vector && logo.logo_vector instanceof File) {
                        formData.append(`logos[${index}][logo_vector]`, logo.logo_vector);
                    }

                    if (hasValidName && logo.name_vector && logo.name_vector instanceof File) {
                        formData.append(`logos[${index}][name_vector]`, logo.name_vector);
                    }

                    if (hasValidNumber && logo.number_vector && logo.number_vector instanceof File) {
                        formData.append(`logos[${index}][number_vector]`, logo.number_vector);
                    }
                }
            }

            for (let pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(`  ${pair[0]} = [File: ${pair[1].name}, size: ${pair[1].size}]`);
                } else {
                    console.log(`  ${pair[0]} = "${pair[1]}"`);
                }
            }

            const response = await API(
                apiUrls.cartUpdateLogos,
                {},
                "POST",
                formData,
                true
            );

            if (response?.data?.success) {
                const transformedItem = transformCartItems([response.data.data])[0];
                return {
                    item: transformedItem,
                    message: response.data.message || 'Cart item updated successfully'
                };
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to update cart item');
            }
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                'Network error occurred'
            );
        }
    }
);

export const removeCartItem = createAsyncThunk(
    'cart/removeCartItem',
    async (removeData, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('cart_id', removeData.itemId);

            const response = await callAPI(
                apiUrls.cartDelete,
                {},
                "POST",
                formData,
                true
            );

            if (response?.data?.success) {
                if (removeData.productId) {
                    removeCustomizationFromLocalStorage(removeData.productId);
                }
                const editingCartItem = sessionStorage.getItem('editing_cart_item');
                if (editingCartItem) {
                    try {
                        const parsed = JSON.parse(editingCartItem);
                        if (parsed.cartItemId === removeData.itemId) {
                            sessionStorage.removeItem('editing_cart_item');
                            console.log('🗑️ Removed editing_cart_item from sessionStorage');
                        }
                    } catch (error) {
                        console.error('Error parsing editing_cart_item:', error);
                    }
                }
                return {
                    cartId: removeData.itemId,
                    message: response.data.message || 'Item removed from cart'
                };
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to remove item');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const clearCart = createAsyncThunk(
    'cart/clearCart',
    async (_, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                apiUrls.cartDelete,
                {},
                "POST",
                {},
                false
            );

            if (response?.data?.success) {
                return {
                    message: response.data.message || 'Cart cleared successfully'
                };
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to clear cart');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const createStripeCheckout = createAsyncThunk(
    'cart/createStripeCheckout',
    async (checkoutData, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                apiUrls.checkoutItems,
                {},
                "POST",
                checkoutData
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createStripeSession = createAsyncThunk(
    'cart/createStripeSession',
    async ({ checkoutId, successUrl, cancelUrl }, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.checkoutPayment}/${checkoutId}`,
                {},
                "POST",
                { success_url: successUrl, cancel_url: cancelUrl }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const verifyStripePayment = createAsyncThunk(
    'cart/verifyStripePayment',
    async (sessionId, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.checkoutVerify}?session_id=${sessionId}`,
                {},
                "GET"
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createPayPalSession = createAsyncThunk(
    'cart/createPayPalSession',
    async ({ checkoutId, successUrl, cancelUrl }, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.checkoutPaypalPayment}/${checkoutId}`,
                {},
                "POST",
                {
                    success_url: successUrl,
                    cancel_url: cancelUrl
                }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const verifyPayPalPayment = createAsyncThunk(
    'cart/verifyPayPalPayment',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.checkoutPaypalVerify}?order_id=${orderId}`,
                {},
                "GET"
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const cartSlice = createSlice({
    name: 'cart',
    initialState: {
        items: [],
        loading: false,
        error: null,
        successMessage: null,
        lastUpdated: null,
        summary: {
            subtotal: 0,
            tax: 0,
            discount: 0,
            total: 0,
            itemCount: 0
        },
        meta: {
            currentPage: 1,
            total: 0,
            perPage: 10,
            lastPage: 1
        }
    },
    reducers: {
        updateQuantityOptimistic: (state, action) => {
            const { cartId, quantity } = action.payload;
            const itemIndex = state.items.findIndex(item => item.cart_id === cartId || item.id === cartId);

            if (itemIndex !== -1) {
                if (quantity <= 0) {
                    state.items.splice(itemIndex, 1);
                } else {
                    state.items[itemIndex].quantity = quantity;
                }
                cartSlice.caseReducers.calculateSummary(state);
            }
        },
        addCustomizationsToCartItem: (state, action) => {
            const { itemId, customizations, previewImages, uploadedFiles } = action.payload;
            const item = state.items.find(item => item.id === itemId);
            if (item) {
                item.customizations = customizations;
                item.previewImages = previewImages;
                item.uploadedFiles = uploadedFiles;
            }
        },
        setCartLoading: (state, action) => {
            state.loading = action.payload;
        },
        saveCartToLocalStorage: (state) => {
            try {
                const cartData = {
                    items: state.items,
                    totalQuantity: state.totalQuantity,
                    totalAmount: state.totalAmount,
                    timestamp: Date.now()
                };
                localStorage.setItem('cart_backup', JSON.stringify(cartData));
            } catch (error) {
                console.error('Error saving cart to localStorage:', error);
            }
        },
        loadCartFromLocalStorage: (state) => {
            try {
                const savedCart = localStorage.getItem('cart_backup');
                if (savedCart) {
                    const cartData = JSON.parse(savedCart);
                    // Check if cart is not expired (optional: 7 days expiry)
                    const isExpired = Date.now() - cartData.timestamp > 7 * 24 * 60 * 60 * 1000;
                    if (!isExpired) {
                        state.items = cartData.items;
                        state.totalQuantity = cartData.totalQuantity;
                        state.totalAmount = cartData.totalAmount;
                    }
                }
            } catch (error) {
                console.error('Error loading cart from localStorage:', error);
            }
        },
        clearCartBackup: (state) => {
            localStorage.removeItem('cart_backup');
        },
        removeItemOptimistic: (state, action) => {
            const cartId = action.payload;
            state.items = state.items.filter(item => !(item.cart_id === cartId || item.id === cartId));
            cartSlice.caseReducers.calculateSummary(state);
        },

        clearCartOptimistic: (state) => {
            state.items = [];
            state.summary = {
                subtotal: 0,
                tax: 0,
                discount: 0,
                total: 0,
                itemCount: 0
            };
        },

        calculateSummary: (state) => {
            const items = Array.isArray(state.items) ? state.items : [];

            const subtotal = items.reduce((total, item) => {
                if (!item) return total;
                const price = Number(item.price) || 0;
                const quantity = Number(item.quantity) || 0;
                return total + (price * quantity);
            }, 0);

            const printingTotal = items.reduce((total, item) => {
                if (!item) return total;
                const printingPrice = Number(item.printingPrice) || 0;
                const quantity = Number(item.quantity) || 0;

                return total + (printingPrice * quantity);
            }, 0);


            const taxRate = 0;
            const tax = taxRate;

            const itemCount = items.reduce((count, item) => {
                if (!item) return count;
                const quantity = Number(item.quantity) || 0;
                return count + quantity;
            }, 0);

            const discount = Number(state.summary?.discount) || 0;

            state.summary = {
                subtotal: subtotal || 0,
                printingTotal: printingTotal || 0,
                tax: tax || 0,
                discount: discount,
                total: Math.max(0, (subtotal || 0) + (printingTotal || 0) + (tax || 0) - discount),
                itemCount: itemCount || 0
            };
            state.lastUpdated = new Date().toISOString();
        },

        applyCoupon: (state, action) => {
            const {
                discountAmount,
                couponCode,
                couponId,
                cartTotal,
                payableTotal,
                couponData
            } = action.payload;

            state.coupon = {
                code: couponCode,
                couponId: couponId,
                discountAmount: discountAmount,
                cartTotal: cartTotal,
                payableTotal: payableTotal,
                couponData: couponData
            };

            state.summary.discount = discountAmount;
            const subtotal = state.summary.subtotal || 0;
            const printingTotal = state.summary.printingTotal || 0;

            const tax = state.summary.tax || 0;
            state.summary.total = Math.max(0, (subtotal + printingTotal + tax) - discountAmount);
            state.summary.appliedCoupon = couponCode;
        },

        removeCoupon: (state) => {
            state.coupon = null;
            state.summary.discount = 0;
            state.summary.total = state.summary.subtotal + state.summary.tax;
            delete state.summary.appliedCoupon;
        },

        clearCartError: (state) => {
            state.error = null;
        },

        clearCartMessage: (state) => {
            state.successMessage = null;
        },

        setCartMeta: (state, action) => {
            state.meta = {
                currentPage: action.payload.current_page || 1,
                total: action.payload.total || 0,
                perPage: action.payload.per_page || 10,
                lastPage: action.payload.last_page || 1
            };
        },

        resetCart: (state) => {
            return {
                ...state,
                items: [],
                error: null,
                successMessage: null,
                summary: {
                    subtotal: 0,
                    tax: 0,
                    discount: 0,
                    total: 0,
                    itemCount: 0
                }
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCart.fulfilled, (state, action) => {
                state.loading = false;
                state.items = Array.isArray(action.payload) ? action.payload : [];
                cartSlice.caseReducers.calculateSummary(state);
            })
            .addCase(fetchCart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(addToCart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addToCart.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;

                const newItem = action.payload.item;
                const tempId = newItem.tempId;

                if (tempId) {
                    state.items = state.items.filter(item => item.tempId !== tempId);
                }

                const existingItemIndex = state.items.findIndex(item =>
                    item.productId === newItem.productId &&
                    item.sizeId === newItem.sizeId &&
                    item.colorId === newItem.colorId
                );

                if (existingItemIndex !== -1) {
                    state.items[existingItemIndex].quantity += newItem.quantity;
                    if (newItem.logos && newItem.logos.length > 0) {
                        state.items[existingItemIndex].logos = newItem.logos;
                    }
                } else {
                    state.items.push(newItem);
                }

                cartSlice.caseReducers.calculateSummary(state);
            })
            .addCase(addToCart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(updateCartItem.pending, (state) => {
                // state.loading = true;
                state.error = null;
            })
            .addCase(updateCartItem.fulfilled, (state, action) => {
                // state.loading = false;
                state.successMessage = action.payload.message;

                const { cartId, quantity } = action.payload;
                const itemIndex = state.items.findIndex(item => item.cart_id === cartId || item.id === cartId);

                if (itemIndex !== -1) {
                    if (quantity <= 0) {
                        state.items.splice(itemIndex, 1);
                    } else {
                        state.items[itemIndex].quantity = quantity;
                        if (action.payload.item) {
                            state.items[itemIndex] = {
                                ...state.items[itemIndex],
                                ...action.payload.item
                            };
                        }
                    }
                    cartSlice.caseReducers.calculateSummary(state);
                }
            })
            .addCase(updateCartItem.rejected, (state, action) => {
                // state.loading = false;
                state.error = action.payload;
            })

            .addCase(updateCartLogos.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateCartLogos.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;

                const { cartId, item } = action.payload;
                const itemIndex = state.items.findIndex(item => item.cart_id === cartId || item.id === cartId);

                if (itemIndex !== -1) {
                    state.items[itemIndex] = {
                        ...state.items[itemIndex],
                        ...item,
                        logos: item.logos
                    };
                    cartSlice.caseReducers.calculateSummary(state);
                }
            })
            .addCase(updateCartLogos.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(removeCartItem.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeCartItem.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                state.items = state.items.filter(item => !(item.cart_id === action.payload.cartId || item.id === action.payload.cartId));
                cartSlice.caseReducers.calculateSummary(state);
            })
            .addCase(removeCartItem.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(clearCart.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(clearCart.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
                state.items = [];
                state.summary = {
                    subtotal: 0,
                    tax: 0,
                    discount: 0,
                    total: 0,
                    itemCount: 0
                };
            })
            .addCase(clearCart.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const {
    updateQuantityOptimistic,
    removeItemOptimistic,
    clearCartOptimistic,
    calculateSummary,
    applyCoupon,
    removeCoupon,
    clearCartError,
    clearCartMessage,
    setCartMeta,
    setCartLoading, 
    resetCart
} = cartSlice.actions;

export const selectCartItems = (state) => state.cart.items || [];
export const selectCartLoading = (state) => state.cart.loading;
export const selectCartError = (state) => state.cart.error;
export const selectCartSuccessMessage = (state) => state.cart.successMessage;
export const selectCartSummary = (state) => state.cart.summary;
export const selectCartItemCount = (state) => state.cart.summary.itemCount;
export const selectCartSubtotal = (state) => state.cart.summary.subtotal;
export const selectCartTax = (state) => state.cart.summary.tax;
export const selectCartDiscount = (state) => state.cart.summary.discount;
export const selectCartTotal = (state) => state.cart.summary.total;
export const selectCartLastUpdated = (state) => state.cart.lastUpdated;
export const selectCartMeta = (state) => state.cart.meta;
export const selectCartCoupon = (state) => state.cart.coupon;
export const selectCartPrintingTotal = (state) => state.cart.summary.printingTotal || 0;
export const selectCartItemById = (state, cartId) => {
    return state.cart.items.find(item => item.cart_id === cartId || item.id === cartId);
};

export const selectIsItemInCart = (state, productId, sizeId = null, colorId = null) => {
    const items = state.cart.items || [];

    if (sizeId !== null && colorId !== null) {
        return items.some(item =>
            item.productId === productId &&
            item.sizeId === sizeId &&
            item.colorId === colorId
        );
    } else if (sizeId !== null) {
        return items.some(item =>
            item.productId === productId &&
            item.sizeId === sizeId
        );
    } else if (colorId !== null) {
        return items.some(item =>
            item.productId === productId &&
            item.colorId === colorId
        );
    } else {
        return items.some(item => item.productId === productId);
    }
};

export default cartSlice.reducer;